import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryState,
} from 'nuqs';
import {
  IVAdjustmentSettings,
  OC_DEFAULT_CALL_SLOPE_MULT,
  OC_DEFAULT_LEVEL_SHIFT,
  OC_DEFAULT_PUT_SLOPE_MULT,
  OC_DEFAULT_SYM,
  PositionLeg,
  PositionShares,
  Position as PositionType,
} from 'types/optionsCalculator';
import {
  shortIdGenerator,
  stringifyLeg,
  stringifyIVSettings,
  parseLegString,
  stringifyShare,
  parseShareString,
  parseIVSettings,
} from 'util/optionsCalculator';
import { dayjs } from 'util/shared/date';
import { useMemo, useCallback } from 'react';

const DEFAULT_BASE_DATE = dayjs().valueOf();

// this is the main hook for the options calculator state
const useOptionsCalculator = () => {
  const [sym, setSym] = useQueryState(
    'sym',
    parseAsString.withDefault(OC_DEFAULT_SYM),
  );
  const [baseDate, setBaseDate] = useQueryState(
    'bd',
    parseAsInteger.withDefault(DEFAULT_BASE_DATE),
  );

  const [legStrings, setLegStrings] = useQueryState(
    'legs',
    parseAsArrayOf(parseAsString).withDefault([]),
  );

  const [sharesString, setSharesString] = useQueryState(
    'shares',
    parseAsArrayOf(parseAsString).withDefault([]),
  );

  const [ivSettingsString, setIvSettingsString] = useQueryState(
    'ivs',
    parseAsArrayOf(parseAsString).withDefault([]),
  );

  // Memoize expensive parsing operations
  const legs = useMemo(() => {
    return legStrings.map(parseLegString).filter(Boolean) as PositionLeg[];
  }, [legStrings]);

  const shares = useMemo(() => {
    return sharesString
      .map(parseShareString)
      .filter(Boolean) as PositionShares[];
  }, [sharesString]);

  const ivSettings = useMemo(() => {
    return ivSettingsString
      .map(parseIVSettings)
      .filter((s): s is IVAdjustmentSettings => s != null);
  }, [ivSettingsString]);

  const updateLegsBulk = useCallback(
    (legIds: number[], updatedFields: Partial<PositionLeg>) => {
      const newLegStrings = legStrings.map((legStr) => {
        const leg = parseLegString(legStr);
        if (leg == null) {
          return legStr;
        }
        if (legIds.includes(leg.leg_id)) {
          const updatedLeg = { ...leg, ...updatedFields };
          return stringifyLeg(updatedLeg);
        }
        return stringifyLeg(leg);
      });
      void setLegStrings(newLegStrings);
    },
    [legStrings, setLegStrings],
  );

  // Memoize callback functions to prevent unnecessary re-renders
  const handleSymChange = useCallback(
    (newSym: string) => {
      void setSym(newSym);
    },
    [setSym],
  );

  const addLeg = useCallback(
    (leg: PositionLeg) => {
      const legWithId = {
        ...leg,
        id: shortIdGenerator(),
      };
      const newLegStrings = [...legStrings, stringifyLeg(legWithId)];
      void setLegStrings(newLegStrings);
    },
    [legStrings, setLegStrings],
  );

  const removeLeg = useCallback(
    (legId: number) => {
      const newLegStrings = legStrings.filter(
        (legStr) => !legStr.endsWith(`_${legId}`),
      );
      void setLegStrings(newLegStrings);
    },
    [legStrings, setLegStrings],
  );

  const removeLegsBulk = useCallback(
    (legIds: number[]) => {
      const newLegStrings = legStrings.filter(
        (legStr) => !legIds.some((id) => legStr.endsWith(`_${id}`)),
      );
      void setLegStrings(newLegStrings);
    },
    [legStrings, setLegStrings],
  );

  const syncIVSettingsWithLegs = useCallback(
    (legStrings: string[]) => {
      const givenLegs = legStrings
        .map(parseLegString)
        .filter(Boolean) as PositionLeg[];
      const expirations = [
        ...new Set(
          givenLegs
            .map((leg) => leg.expiry)
            .filter((expiration): expiration is number => expiration != null),
        ),
      ];

      // Create a map to avoid duplicates
      const newIvSettingsMap = new Map<number, string>();

      expirations.forEach((expiration) => {
        const settings = ivSettings.find((s) => s.expiry === expiration);
        if (settings != null) {
          newIvSettingsMap.set(expiration, stringifyIVSettings(settings));
        } else {
          newIvSettingsMap.set(
            expiration,
            stringifyIVSettings({
              expiry: expiration,
              level_shift: OC_DEFAULT_LEVEL_SHIFT,
              call_slope_mult: OC_DEFAULT_CALL_SLOPE_MULT,
              put_slope_mult: OC_DEFAULT_PUT_SLOPE_MULT,
            }),
          );
        }
      });

      void setIvSettingsString(Array.from(newIvSettingsMap.values()));
    },
    [ivSettings, setIvSettingsString],
  );

  const updateLeg = useCallback(
    (legId: number, updatedFields: Partial<PositionLeg>) => {
      const newLegStrings = legStrings.map((legStr) => {
        if (legStr.endsWith(`_${legId}`)) {
          const oldLeg = parseLegString(legStr);
          if (oldLeg == null) {
            return legStr;
          }
          const updatedLeg = { ...oldLeg, ...updatedFields };
          return stringifyLeg(updatedLeg);
        }
        return legStr;
      });
      syncIVSettingsWithLegs(newLegStrings);
      void setLegStrings(newLegStrings);
    },
    [legStrings, setLegStrings, syncIVSettingsWithLegs],
  );

  const clearLegs = useCallback(() => {
    void setLegStrings([]);
  }, [setLegStrings]);

  const updateShares = useCallback(
    (shares: PositionShares[]) => {
      void setSharesString(shares.map(stringifyShare));
    },
    [setSharesString],
  );

  const updateIVSettings = useCallback(
    (ivSettings: Partial<IVAdjustmentSettings>) => {
      const newSettingsString = stringifyIVSettings(ivSettings);

      // Remove any existing settings for this expiration and add the new one
      const filteredSettings = ivSettingsString.filter(
        (settingsStr) => !settingsStr.startsWith(`${ivSettings.expiry}:`),
      );

      const newIvSettingsStrings = [...filteredSettings, newSettingsString];

      void setIvSettingsString(newIvSettingsStrings);
    },
    [ivSettingsString, setIvSettingsString],
  );

  const updateBaseDate = useCallback(
    (newBaseDate: number) => {
      void setBaseDate(newBaseDate);
    },
    [setBaseDate],
  );

  const setActivePosition = useCallback(
    (position: PositionType) => {
      if (position == null) {
        return;
      }
      void setSym(position.sym);
      // Set legs
      const legStrings = position.option_legs?.map(stringifyLeg) ?? [];
      void setLegStrings(legStrings);

      // Set shares
      const sharesStrings =
        position.underlying_shares?.map(stringifyShare) ?? [];
      void setSharesString(sharesStrings);

      // Set IV settings
      const ivStrings = position.iv_adjustments.map(stringifyIVSettings);
      void setIvSettingsString(ivStrings);
    },
    [setLegStrings, setSharesString, setIvSettingsString, setSym],
  );

  // Return memoized object to prevent unnecessary re-renders
  return useMemo(
    () => ({
      sym,
      baseDate,
      legs,
      shares,
      ivSettings,
      handleSymChange,
      updateIVSettings,
      addLeg,
      removeLeg,
      updateLeg,
      updateLegsBulk,
      clearLegs,
      removeLegsBulk,
      updateShares,
      updateBaseDate,
      setActivePosition,
    }),
    [
      sym,
      baseDate,
      legs,
      shares,
      ivSettings,
      handleSymChange,
      updateIVSettings,
      addLeg,
      removeLeg,
      updateLeg,
      updateLegsBulk,
      clearLegs,
      removeLegsBulk,
      updateShares,
      updateBaseDate,
      setActivePosition,
    ],
  );
};

export default useOptionsCalculator;
